/**
 * @fileoverview Org portal — one class inside a program.
 *
 * A class arrives here straight from being created, because a class without a
 * schedule is not finished: the next thing to do is give it one, and this is
 * where that happens.
 *
 * **Three tabs, because a class is asked three different questions.** How is it
 * going (the totals, the roster, the schedule); when does it meet (the term,
 * in the same list/week/month the org calendar and the instructor's calendar
 * use); and what has been taught (the lessons). It was one long scroll, which
 * put "who is enrolled" and "what happened in October" in the same breath and
 * pushed the meetings that matter below a roster that rarely changes.
 *
 * The org can fill the roster, because that is the other half of setting a
 * class up and the list already exists in a spreadsheet somewhere. What it
 * cannot do is teach: the shared lesson and the teaching history belong to the
 * instructor, and the org watches what comes back. So the Lessons tab shows
 * that the work was done, by whom and when — and never the summary itself,
 * which is a child's lesson and takes the `lesson_notes` viewer grant.
 *
 * Consent is shown beside every student on purpose. It is the one fact that
 * decides whether this class can be recorded at all, and an imported roster
 * starts with none of it.
 *
 * **A meeting opens its details first** (MeetingDetailsDialog, as on every
 * calendar): Edit moves or cancels it; "Prep note & plan" opens the meeting's
 * own editor, where the note to the instructor and the lesson plan live. The
 * org Calendar's "Open class" lands here with `?meeting=<occurrence_id>`,
 * which opens that editor straight away.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { useTabParam } from "@/lib/tabParam";
import { Card, PageShell, SectionHeader, ListRow, Icon, Tag, StatCard, SegmentedControl, Button } from "@/components/studio/primitives";
import MeetingCalendar, { rangeFor, weekStart } from "@/components/org/MeetingCalendar";
import MeetingDetailsDialog, { addressLine } from "@/components/org/MeetingDetailsDialog";
import ClassScheduleBuilder from "@/components/org/ClassScheduleBuilder";
import OrganizationClassScheduling from "@/components/org/OrganizationClassScheduling";
import AssignSubstituteDialog from "@/components/org/AssignSubstituteDialog";
import SubstituteLessonDialog from "@/components/org/SubstituteLessonDialog";
import { coverName } from "@/lib/substitutes";
import ImportRosterDialog from "@/components/org/ImportRosterDialog";
import ClassInstructorsCard from "@/components/org/ClassInstructorsCard";
import ClassDetailsCard from "@/components/org/ClassDetailsCard";
import ClassPrepNotesCard from "@/components/org/ClassPrepNotesCard";
import ClassArchiveCard from "@/components/org/ClassArchiveCard";
import RosterAnnotationFields from "@/components/org/RosterAnnotationFields";
import LessonFilterBar from "@/components/studio/LessonFilterBar";
import { filterLessons, lessonStatus, missingRow, sortChronologically } from "@/lib/lessonHistory";
import useOrgSessionActions, { ArchivedNote } from "@/components/org/useOrgSessionActions";
import { clearanceLine, clearanceTag, isCleared } from "@/lib/notesClearance";
import AssignedClassCalendar, { AssignedMeetingEditor } from "@/components/org/AssignedClassCalendar";
import CurriculumCard from "@/components/CurriculumCard";
import ResourcesSection from "@/components/org/ResourcesSection";
import ExportMenu, { classExportItems } from "@/components/studio/ExportMenu";
import AnnouncementDialog from "@/components/org/AnnouncementDialog";

const KIND_LABELS = { studio_class: "Class", ensemble: "Ensemble", cohort: "Cohort" };

/* The server's word for each of this tab's row statuses, as the Lessons
   export writes it (lib/orgSessions `SESSION_EXPORT_COLUMNS`). */
const EXPORT_STATUS = { written: "taught", planned: "planned", missing: "no notes", cleared: "notes cleared", cancelled: "cancelled" };

/** One of this tab's rows in the Lessons export's columns. */
function classExportRow(row, detail) {
  const written = Boolean(row.title) && !row.missing && !row.notes_cleared && row.status !== "cancelled";
  return {
    date: row.scheduled_date || row.date,
    time_local: row.time_local || "",
    program_title: detail?.program_title || "",
    class_name: detail?.class?.name || "",
    instructor_name: row.instructor_name || "",
    substitute_name: row.substitute?.name || "",
    session_status: EXPORT_STATUS[lessonStatus(row)] || "",
    attendance: written ? row.students : "",
    roster_size: (detail?.class?.member_ids || []).length,
    minutes: written ? Math.round(((row.duration_seconds || 0) / 60) * 10) / 10 : "",
    title: written ? row.title : "",
    shared: written ? (row.shared ? "yes" : "no") : "",
    archived: Boolean(row.archived),
  };
}

/* Five questions a class is asked, one tab each. Settings is where the class
   is SET UP — details, the term builder, publishing, deletion — so that
   Overview can answer "how is it going" without a form in the way. */
const TABS = [
  { id: "overview", label: "Overview", testId: "class-tab-overview" },
  { id: "calendar", label: "Calendar", testId: "class-tab-calendar" },
  { id: "lessons", label: "Lessons", testId: "class-tab-lessons" },
  { id: "students", label: "Students", testId: "class-tab-students" },
  { id: "settings", label: "Settings", testId: "class-tab-settings" },
];

/* Consent is a three-way answer, and "asked and waiting" reads very
   differently from "never asked" — collapsing them would hide the work left
   to do on an imported roster. */
const CONSENT_LABELS = {
  granted: "Consent",
  requested: "Asked",
  declined: "Declined",
  revoked: "Withdrawn",
  none: "No consent",
};

/** "Sep 18" from a YYYY-MM-DD, without tripping over UTC parsing. */
function shortDate(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return ymd || "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** "15:30" as "3:30 PM". */
function clock(timeLocal) {
  const [h, m] = String(timeLocal || "").split(":").map(Number);
  if (!Number.isFinite(h)) return "";
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

export default function OrgClassDetailPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [substitutes, setSubstitutes] = useState([]);
  const [assigning, setAssigning] = useState(null);
  /* A covered session's notes (docs/substitute-requests.md): the org reads,
     writes and sends them to families from here. */
  const [subLesson, setSubLesson] = useState(null);
  const [editingMeeting, setEditingMeeting] = useState(null);
  // The meeting whose details are open (MeetingDetailsDialog).
  const [picked, setPicked] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [roster, setRoster] = useState([]);
  /* The org's own line about the annotation fields, verbatim from the server
     (decision D4), so the portal and the API say the same thing. */
  const [rosterHelp, setRosterHelp] = useState("");
  /* The Lessons tab's filter (F4). The status lives in the URL as the tab's
     filter — the Missing notes tile sets it — and the rest here. */
  const [lessonBar, setLessonBar] = useState({ period: "all", instructor: null, search: "" });
  const [chasing, setChasing] = useState(false);
  const [chased, setChased] = useState(null);
  const [importing, setImporting] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  /* The class's teaching materials: its curriculum revisions and the plans on
     its meetings. An empty list when the feature is switched off. */
  const [materials, setMaterials] = useState([]);
  /* The tab and its filter live in the URL (F6): a tile that opens a tab, a
     refresh and the Back button all agree. */
  const { tab, filter, setTab } = useTabParam(TABS, "overview");

  /* An annotation saved (or rolled back) on one row, without a reload: the
     fields are optimistic, and a full refetch would blank what the
     administrator is still typing on the next row. */
  const annotationSaved = useCallback((studentId, annotation) => {
    setRoster((rows) => rows.map((m) => (m.student_id === studentId ? { ...m, annotation } : m)));
  }, []);

  /* The Students section's downloads: the roster as the tab shows it, and
     this one class's attendance. */
  const exportItems = classExportItems(`/org/classes/${classId}`, detail?.class?.name);
  const [view, setView] = useState("list");
  const [anchor, setAnchor] = useState(() => weekStart(new Date()));
  /* The meetings of the window the week or month view is looking at, keyed by
     that window. The page's own read returns an assigned class's whole term,
     but a slot-based class only comes back 90 days either side of today, and
     a term is longer than that. */
  const [windowed, setWindowed] = useState(null);

  const loadMaterials = useCallback(async () => {
    const res = await orgApi.get(`/org/classes/${classId}/materials`).catch(() => ({ data: { materials: [] } }));
    setMaterials(res.data?.materials || []);
  }, [classId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, subs, members] = await Promise.all([
        orgApi.get(`/org/classes/${classId}`),
        orgApi.get("/org/substitutes").catch(() => ({ data: { substitutes: [] } })),
        orgApi.get(`/org/classes/${classId}/roster`).catch(() => ({ data: { members: [] } })),
        loadMaterials(),
      ]);
      setDetail(res.data);
      setRoster(members.data?.members || []);
      setRosterHelp(members.data?.annotation_help || "");
      setSubstitutes((subs.data?.substitutes || []).filter((x) => x.active !== false));
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Class not found");
        navigate("/org/programs");
        return;
      }
      toast.error(getOrgErrorMessage(err, "Couldn't load this class"));
    } finally {
      setLoading(false);
    }
  }, [classId, navigate, loadMaterials]);

  useEffect(() => { load(); }, [load]);

  /* The week and month views ask the server for the window they show, exactly
     as the org calendar does through `rangeFor`. The list view is the whole
     term, not a window, so it stays on what the page loaded. Re-run after a
     reload too: a substitute recorded through the editor must show up in the
     month it was recorded against. */
  const range = useMemo(() => (view === "list" ? null : rangeFor(view, anchor)), [view, anchor]);
  const rangeKey = range ? `${classId}:${range.start}:${range.end}` : null;
  useEffect(() => {
    if (tab !== "calendar" || !range) return undefined;
    let stale = false;
    orgApi
      .get(`/org/classes/${classId}?start=${range.start}&end=${range.end}`)
      .then((res) => {
        if (stale) return;
        setWindowed({
          key: `${classId}:${range.start}:${range.end}`,
          meetings: [...(res.data?.past || []), ...(res.data?.upcoming || [])],
        });
      })
      .catch(() => {
        /* The term the page already loaded stands in for the window. */
      });
    return () => { stale = true; };
  }, [tab, range, classId, detail]);

  // Memoized rather than defaulted inline: a fresh [] on every render would
  // rebuild the calendar's events (and its lane layout) on every keystroke
  // anywhere on the page.
  const upcoming = useMemo(() => detail?.upcoming || [], [detail]);
  const past = useMemo(() => detail?.past || [], [detail]);

  /* Everyone teaching the class now. `instructor_name` remains the answer for
     a class staffed before co-teaching existed, and for one whose instructors
     have all stepped back — a term with nobody on it is a real state and says
     so rather than printing an empty name. */
  const instructors = useMemo(() => detail?.instructors || [], [detail]);
  const activeInstructors = useMemo(
    () => instructors.filter((i) => i.status !== "inactive"),
    [instructors],
  );
  const taughtBy = activeInstructors.length
    ? activeInstructors.map((i) => i.name).join(" and ")
    : detail?.instructor_name || "nobody yet";


  /* A past meeting that happened and was never written up. Cancelled sessions
     owe nothing — a snow day is part of the record and did not need notes. A
     meeting the organization deleted from its lists is not listed at all
     (useOrgSessionActions); the calendar still shows it, as the schedule. */
  const listedPast = useMemo(() => past.filter((m) => !m.deleted), [past]);
  const missingSessions = useMemo(
    () => listedPast.filter((m) => m.status !== "cancelled" && !m.has_notes && !isCleared(m)),
    [listedPast],
  );
  const notesMissing = missingSessions.length;
  /* Past meetings nobody wrote up whose notice was cleared (lib/notesClearance):
     listed as cleared, never counted as missing. */
  const clearedSessions = useMemo(
    () => listedPast.filter((m) => m.status !== "cancelled" && !m.has_notes && isCleared(m)),
    [listedPast],
  );

  /* One list for the Lessons tab (O8): the lessons the org may see (title,
     date, who — never the summary), the past sessions nobody wrote up as
     rows of their own, and the cancelled ones, so "what is outstanding" is
     answered in one place through the same filter every lessons list uses. */
  const lessonRows = useMemo(() => {
    const cls = detail?.class || {};
    const activeIds = activeInstructors.map((i) => i.user_id);
    const written = (detail?.lessons || []).map((l) => ({
      ...l,
      kind: "class",
      class_id: cls.class_id,
      user_id: l.instructor_id,
      created_at: l.date ? `${l.date}T12:00:00` : undefined,
      scheduled_date: l.date,
      email_sent: l.shared,
    }));
    const outstanding = missingSessions.map((m) =>
      missingRow(m, { classId: cls.class_id, className: cls.name, instructorIds: activeIds, instructorName: taughtBy }),
    );
    const cancelled = listedPast
      .filter((m) => m.status === "cancelled")
      .map((m) => missingRow(m, { classId: cls.class_id, className: cls.name, instructorIds: activeIds, status: "cancelled" }));
    const cleared = clearedSessions.map((m) =>
      missingRow(m, { classId: cls.class_id, className: cls.name, instructorIds: activeIds, instructorName: taughtBy }),
    );
    return [...written, ...outstanding, ...cleared, ...cancelled];
  }, [detail, missingSessions, clearedSessions, listedPast, activeInstructors, taughtBy]);
  const lessonStatusFilter =
    filter && ["written", "missing", "cleared", "planned", "cancelled", "archived"].includes(filter) ? filter : "all";
  /* The term top to bottom, the way it was taught. An archived session is
     listed under Archived alone (useOrgSessionActions). */
  const shownLessons = useMemo(() => {
    const archivedOnly = lessonStatusFilter === "archived";
    const listed = lessonRows.filter((r) => Boolean(r.archived) === archivedOnly);
    return sortChronologically(
      filterLessons(
        listed,
        { ...lessonBar, kind: "all", status: archivedOnly ? "all" : lessonStatusFilter },
        () => detail?.class?.name || "",
      ),
    );
  }, [lessonRows, lessonBar, lessonStatusFilter, detail]);
  /* Ticking missing sessions and clearing their notices (useOrgNotesClear).
     A refresh re-reads the class only, so the page does not blank. */
  const refreshClass = useCallback(
    () =>
      orgApi
        .get(`/org/classes/${classId}`)
        .then((res) => setDetail(res.data))
        .catch(() => {}),
    [classId],
  );
  const clearing = useOrgSessionActions({
    rows: shownLessons,
    onChanged: refreshClass,
    testId: "class-notes-clear",
    exportName: `hans-${(detail?.class?.name || "class").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-sessions-selected`,
    toExportRow: (row) => classExportRow(row, detail),
  });
  const lessonInstructors = useMemo(() => {
    const seen = new Map();
    for (const i of instructors) seen.set(i.user_id, i.name);
    for (const l of detail?.lessons || []) if (l.instructor_id && !seen.has(l.instructor_id)) seen.set(l.instructor_id, l.instructor_name || l.instructor_id);
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [instructors, detail]);

  /* Ask the class's instructors for the notes they owe (decision D5): no
     email — it lands in their updates feed, once per class until they act. */
  const chaseNotes = async () => {
    setChasing(true);
    try {
      const res = await orgApi.post(`/org/classes/${classId}/notes-chase`);
      const names = (res.data?.chased || []).map((c) => c.name).join(" and ");
      setChased({ names, at: res.data?.at });
      toast.success(names ? `Asked ${names} for notes` : "Nobody is teaching this class to ask");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't send the reminder"));
    } finally {
      setChasing(false);
    }
  };

  /* The whole term as the shared calendar takes it — one component for the org
     calendar, one class's calendar and, eventually, anything else that draws
     meetings. Pressing a meeting records who covered it, which is the only
     thing an org may do to a session it did not teach. The week and month
     views draw the window they asked for once it has arrived; until then, and
     in the list view, they draw the term. */
  const termMeetings = useMemo(() => [...past, ...upcoming], [past, upcoming]);
  const shownMeetings = rangeKey && windowed?.key === rangeKey ? windowed.meetings : termMeetings;
  const events = useMemo(
    () =>
      shownMeetings.map((m) => ({
        id: m.occurrence_id || `${m.date}-${m.time_local}`,
        date: m.date,
        time_local: m.time_local,
        duration_minutes: m.duration_minutes,
        title: detail?.class?.name || "Class",
        subtitle: m.substitute ? `Covered by ${coverName(m.substitute)}` : taughtBy,
        color: "var(--accent-yellow)",
        muted: m.status === "cancelled",
        tags: [
          m.status === "cancelled" ? { label: "No class" } : null,
          m.substitute ? { label: "Substitute", accent: true } : null,
          m.status !== "cancelled" && m.has_notes === false && !isCleared(m) ? { label: "No notes", accent: true } : null,
          // The org's brief on this meeting (O7), so a term list shows at a
          // glance which sessions have been thought about.
          m.prep_note ? { label: "Note" } : null,
          m.plan ? { label: "Plan" } : null,
        ].filter(Boolean),
        onPress: () => setPicked(m),
      })),
    [shownMeetings, detail, taughtBy],
  );

  /* Arriving from the org Calendar's "Open class": that meeting's editor, open,
     once the term it belongs to has loaded. The parameter is spent on the way
     in, so closing the editor and refreshing does not reopen it. */
  const meetingParam = searchParams.get("meeting");
  useEffect(() => {
    if (!meetingParam || !detail) return;
    const meeting = termMeetings.find((m) => m.occurrence_id === meetingParam);
    if (meeting) setEditingMeeting(meeting);
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete("meeting");
        return out;
      },
      { replace: true },
    );
  }, [meetingParam, detail, termMeetings, setSearchParams]);

  /* One meeting's move, cancel or restore from its details popup, at the
     term revision the page read. A refusal stays in the popup. */
  const saveMeeting = useCallback(
    async (m, changes, done) => {
      try {
        await orgApi.put(`/org/classes/${classId}/meetings/${m.occurrence_id}`, {
          expected_revision: detail?.class?.term_revision || 0,
          ...changes,
        });
      } catch (err) {
        throw new Error(getOrgErrorMessage(err, "Couldn't change this meeting"), { cause: err });
      }
      toast.success(done);
      load();
    },
    [classId, detail, load],
  );

  if (loading) {
    return <div className="mx-auto max-w-3xl px-4 py-8 md:px-8 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>;
  }
  if (!detail?.class) return null;

  const cls = detail.class;
  const meetings = upcoming;
  // Booking ownership is explicit and never inferred from an empty class.
  const assigned = (cls.scheduling_mode || (cls.scheduler_offering_id && cls.enrollment_mode !== "org_managed" ? "booked" : "assigned")) === "assigned";
  const usesNamesRoster = !assigned && cls.enrollment_mode === "org_managed";
  const cleared = roster.filter((m) => m.consent_status === "granted").length;
  // An all-clear is only worth saying when it is one, so the subtitle states
  // the gap rather than a percentage nobody can act on.
  const consentSub = roster.length
    ? cleared === roster.length
      ? "all cleared to record"
      : `${roster.length - cleared} without consent`
    : undefined;

  return (
    <PageShell width="wide">
      <button
        type="button"
        onClick={() => navigate(`/org/programs/${cls.program_id}`)}
        className="mb-4 inline-flex items-center gap-1 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <Icon name="chevronLeft" size={15} /> {detail.program_title || "Program"}
      </button>

      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">{cls.name}</h1>
            <Tag>{KIND_LABELS[cls.kind] || cls.kind}</Tag>
            {cls.archived_at && <Tag data-testid="class-archived-tag">Archived</Tag>}
          </div>
          {/* A class announcement: this class's instructors, by the channels
              the org's Notifications settings allow. */}
          {cls.program_id && !cls.archived_at && (
            <Button size="sm" icon="bell" onPress={() => setAnnouncing(true)} data-testid="open-class-announcement">
              Send announcement
            </Button>
          )}
        </div>
        {cls.description && (
          <p className="mt-1.5 max-w-3xl font-sans text-[14px] text-[var(--text-secondary)]">{cls.description}</p>
        )}
        <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]">
          Taught by {taughtBy}
        </p>
      </div>

      {/* Five tabs are wider than a phone. The bar scrolls within itself on
          a narrow screen rather than pushing the page sideways. */}
      <div className="-mx-4 mb-6 overflow-x-auto px-4 md:mx-0 md:px-0" data-testid="class-tabs-scroller">
        <SegmentedControl testId="class-tabs" className="whitespace-nowrap" value={tab} onChange={(id) => setTab(id)} options={TABS} />
      </div>

      {/* ---- Overview ------------------------------------------------- */}
      {/* How it is going: the vital signs first, each a door into the tab
          that holds the rows behind its number, then who teaches it. */}
      {tab === "overview" && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="class-tiles">
            <StatCard
              compact
              value={detail.sessions_taught ?? 0}
              label="Sessions taught"
              data-testid="class-tile-taught"
              onPress={() => setTab("lessons")}
            />
            {!assigned && cls.scheduler_offering_id ? (
              <StatCard compact value={cls.student_capacity} label="Capacity" data-testid="class-tile-capacity" />
            ) : (
              <StatCard
                compact
                value={meetings.length}
                label="Upcoming"
                data-testid="class-tile-upcoming"
                onPress={() => setTab("calendar")}
              />
            )}
            <StatCard
              compact
              value={roster.length || (cls.member_ids || []).length}
              label="Students"
              sub={consentSub}
              data-testid="class-tile-students"
              onPress={() => setTab("students")}
            />
            {/* The only tile that asks for something. It is here rather than only
                on the program page because this is where an administrator lands
                when they go looking for the class that is behind. */}
            <StatCard
              compact
              value={notesMissing}
              label="Missing notes"
              sub={notesMissing ? "sessions not written up" : "nothing outstanding"}
              data-testid="class-missing-notes"
              onPress={notesMissing ? () => setTab("lessons", "missing") : undefined}
            />
          </div>

          {/* ---- Instructors --------------------------------------------- */}
          {/* An unstaffed class cannot be scheduled, recorded or written up,
              and a handover mid-term is the change an administrator comes
              here to make. */}
          <ClassInstructorsCard
            cls={cls}
            instructors={instructors}
            programInstructors={detail.program_instructors || []}
            onChanged={load}
          />

          {/* ---- Curriculum ---------------------------------------------- */}
          {/* What the class teaches, kept as revisions (O10). The org and the
              class's instructors both read every one and both may add one. */}
          <CurriculumCard
            client={orgApi}
            basePath={`/org/classes/${classId}`}
            role="org_admin"
            materials={materials}
            onChanged={loadMaterials}
            errorMessage={getOrgErrorMessage}
            className="mt-6"
          />

          {/* ---- Other resources ----------------------------------------- */}
          {/* The room itself: a classroom layout, where the stands live.
              This class's instructors see them as tiles on their class page. */}
          <div className="mt-6">
            <ResourcesSection
              basePath={`/org/classes/${classId}`}
              scope="class"
              examples="a classroom layout, where the instruments are stored"
            />
          </div>

          {/* ---- Prep notes --------------------------------------------- */}
          {/* Last, and absent when the class has none. Briefs are written on
              the meeting (Calendar tab); this gathers them beside the plans
              the instructors are working from. */}
          <ClassPrepNotesCard
            meetings={upcoming}
            /* An instructor's plan is filed under the session's teaching id;
               `meeting_occurrence_id` is the term meeting it belongs to. */
            plans={(detail.instructor_plans || []).filter(
              (n) => !n.date || upcoming.some((m) => m.occurrence_id === (n.meeting_occurrence_id || n.occurrence_id)),
            )}
            onOpenMeeting={(m) => {
              if (!m.occurrence_id) return;
              setTab("calendar");
              setEditingMeeting(m);
            }}
          />
        </>
      )}

      {/* ---- Calendar ------------------------------------------------- */}
      {/* The same list/week/month the org calendar and the instructor's
          calendar use. Upcoming meetings used to live halfway down the page
          under the roster; a term belongs in a calendar. */}
      {tab === "calendar" && (
        <>
          {/* "No meetings" is about the TERM, never about the month in view:
              an empty April must keep its arrows, or nobody can reach May. */}
          {termMeetings.length === 0 ? (
            <Card data-testid="class-calendar-empty">
              <p className="font-sans text-[15px] text-[var(--text-tertiary)]">
                No meetings yet — set the schedule under Settings.
              </p>
            </Card>
          ) : (
            <MeetingCalendar
              events={events}
              view={view}
              onViewChange={setView}
              anchor={anchor}
              onAnchorChange={setAnchor}
              loading={Boolean(rangeKey) && windowed?.key !== rangeKey}
              emptyText="No class"
              /* One class is a term, one sequence: the flat list (decision
                 D2), with the date on each row and no week to page through. */
              listStyle="flat"
            />
          )}
        </>
      )}

      {/* ---- Lessons -------------------------------------------------- */}
      {/* What has been taught, and what is outstanding, in one list under the
          one filter bar (O8). Titles, dates and who — never the summary,
          which is a child's lesson and takes the lesson-notes viewer grant. */}
      {tab === "lessons" && (
        <>
          <SectionHeader
            action={notesMissing > 0 ? (chasing ? "Asking…" : "Chase notes") : undefined}
            onAction={chasing ? undefined : chaseNotes}
            actionTestId="class-chase-notes"
          >
            Lessons ({shownLessons.length})
            {notesMissing > 0 && (
              <span className="ml-3 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)]" data-testid="class-lessons-missing-count">
                {notesMissing} missing notes
              </span>
            )}
          </SectionHeader>
          <p className="mb-3 max-w-2xl font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="class-lessons-boundary">
            Titles, dates and who taught each session. What was said in a lesson takes the lesson-notes viewer grant and
            is never listed here.
            {chased && (
              <span className="ml-1" data-testid="class-chase-sent">
                Asked {chased.names} for the missing notes — it is in their updates, not an email.
              </span>
            )}
          </p>
          <LessonFilterBar
            value={{ ...lessonBar, status: lessonStatusFilter }}
            onChange={(next) => {
              if (next.status !== lessonStatusFilter) setTab("lessons", next.status === "all" ? undefined : next.status);
              setLessonBar({ period: next.period, instructor: next.instructor, search: next.search });
            }}
            instructors={lessonInstructors}
            statuses={[
              { id: "all", label: "All" },
              { id: "written", label: "Written" },
              { id: "missing", label: "Missing notes" },
              { id: "cleared", label: "Notes cleared" },
              { id: "planned", label: "Planned" },
              { id: "cancelled", label: "Cancelled" },
              { id: "archived", label: "Archived" },
            ]}
            searchPlaceholder="Search lessons"
          />
          {lessonStatusFilter === "archived" && <ArchivedNote testId="class-archived-note" />}
          <Card padding={0} data-testid="class-lessons">
            {shownLessons.length > 0 && clearing.toolbar}
            {shownLessons.length === 0 ? (
              <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                {lessonRows.length === 0
                  ? "Nothing written up yet. Lessons appear here as the instructor records or types them."
                  : lessonStatusFilter === "missing"
                    ? "Every past session has notes."
                    : "Nothing matches these filters."}
              </div>
            ) : (
              shownLessons.map((l, i) => {
                const st = lessonStatus(l);
                if (st === "missing" || st === "cancelled" || st === "cleared") {
                  /* A covered session with no notes yet is the substitute's to
                     write, or the org's — never the regular instructor's. */
                  const covered = st === "missing" && l.substitute;
                  // An instructor's request to clear it, or its cleared notice.
                  const clearance = clearanceTag(l.notes_clearance);
                  return (
                    <ListRow
                      key={l.lesson_id}
                      borderBottom={i < shownLessons.length - 1}
                      data-testid={`class-lesson-${l.lesson_id}`}
                      leading={clearing.checkbox(l)}
                      title={shortDate(l.scheduled_date)}
                      subtitle={[
                        clock(l.time_local),
                        l.substitute
                          ? `Covered by ${l.substitute.name || "a substitute (to be confirmed)"}`
                          : st === "cancelled"
                            ? "Cancelled"
                            : taughtBy,
                        clearanceLine(l.notes_clearance),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      trailing={
                        <span className="flex items-center gap-2">
                          {l.archived && <Tag>Archived</Tag>}
                          {st === "missing" ? (
                            <>
                              {covered && <Tag>Substitute</Tag>}
                              <Tag accent>{clearance ? clearance.label : "No notes"}</Tag>
                            </>
                          ) : st === "cleared" ? (
                            <Tag>Notes cleared</Tag>
                          ) : (
                            <Tag>No class</Tag>
                          )}
                        </span>
                      }
                      onPress={covered ? () => setSubLesson({ date: l.scheduled_date }) : undefined}
                    />
                  );
                }
                return (
                  <ListRow
                    key={l.lesson_id}
                    borderBottom={i < shownLessons.length - 1}
                    data-testid={`class-lesson-${l.lesson_id}`}
                    leading={clearing.checkbox(l)}
                    title={l.title}
                    /* What an organization is entitled to: that the work was done,
                       how many it reached and who missed it. Never the summary —
                       that is a child's lesson, and reading it takes the
                       `lesson_notes` viewer grant. */
                    subtitle={[
                      `${l.students} student${l.students === 1 ? "" : "s"}`,
                      l.absent_count ? `${l.absent_count} absent` : null,
                      l.excused_count ? `${l.excused_count} excused` : null,
                      l.substitute ? `Taught by ${l.substitute.name}, substitute` : l.instructor_name,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    meta={shortDate(l.date)}
                    /* A substitute's lesson is the org's to read and send; an
                       instructor's summary stays behind the viewer grant. */
                    onPress={l.substitute ? () => setSubLesson({ date: l.date }) : undefined}
                    trailing={
                      <span className="flex items-center gap-2">
                        {l.archived && <Tag>Archived</Tag>}
                        {l.substitute && <Tag>Substitute</Tag>}
                        {/* Written ahead of its session: listed, labelled, and
                            not counted as taught anywhere on this page. */}
                        {l.planned && <Tag>Planned</Tag>}
                        {l.manual && <Tag>Typed</Tag>}
                        {l.shared && <Tag accent>Shared</Tag>}
                      </span>
                    }
                  />
                );
              })
            )}
          </Card>
          {clearing.dialogs}
        </>
      )}

      {/* ---- Students ------------------------------------------------- */}
      {/* The roster is optional and available even before anyone is enrolled.
          Consent sits beside every name because it is the one fact that
          decides whether the class can be recorded. */}
      {tab === "students" &&
        (usesNamesRoster ? (
          <Card data-testid="class-students-scheduler">
            <p className="font-sans text-[15px] text-[var(--text-tertiary)]">
              This class&rsquo;s roster comes from its Scheduler meetings. Manage it under Settings.
            </p>
          </Card>
        ) : (
          <>
            <SectionHeader
              action="+ Add students"
              onAction={() => setImporting(true)}
              actionTestId="open-import-roster"
            >
              Students ({roster.length})
            </SectionHeader>
            {/* What the three fields under each name are for, in the org's own
                words (decision D4), and the way out to a spreadsheet. */}
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <p className="max-w-2xl font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="roster-annotation-help">
                {rosterHelp || "Operational notes for whoever teaches this class: rental serial numbers, access needs, allergies. Visible to the class's instructors; never shown to families."}
              </p>
              {roster.length > 0 && (
                <ExportMenu client={orgApi} items={exportItems} errorMessage={getOrgErrorMessage} testId="export-roster" />
              )}
            </div>
            <Card padding={0} className="mb-6">
              {roster.length === 0 ? (
                <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
                  Nobody is enrolled yet. Paste the roster or drop in a CSV — adding students isn&rsquo;t consent, so
                  nothing can be recorded until their families say yes.
                </div>
              ) : (
                roster.map((m, i) => (
                  <div key={m.student_id} className={i < roster.length - 1 ? "border-b border-[var(--studio-divider)]" : ""}>
                    <ListRow
                      title={m.name}
                      subtitle={m.imported ? "Imported — not reviewed yet" : undefined}
                      trailing={
                        <span className="flex items-center gap-2">
                          {m.archived && <Tag>Archived</Tag>}
                          <Tag accent={m.consent_status === "granted"}>
                            {CONSENT_LABELS[m.consent_status] || m.consent_status}
                          </Tag>
                        </span>
                      }
                    />
                    {/* The org's instrument, serial and note (F2), saved in
                        place. Never the instructor's own notes. */}
                    <div className="-mt-1 px-[10px] pb-2.5">
                      <RosterAnnotationFields
                        classId={classId}
                        studentId={m.student_id}
                        annotation={m.annotation || null}
                        onSaved={annotationSaved}
                      />
                    </div>
                  </div>
                ))
              )}
            </Card>
          </>
        ))}

      {/* ---- Settings ------------------------------------------------- */}
      {/* How the class is set up: its details, the term, whether the term is
          on the calendar, and the way out. The program's address is not
          here — it belongs to the program, and the publish gate says where. */}
      {tab === "settings" && (
        <>
          <ClassDetailsCard key={`${cls.class_id}-${cls.room}-${cls.notes}`} cls={cls} onChanged={load} />
          {/* Set schedule: the form until the class has a term, then the term
              collapsed with Change, which opens on it (`detail.term`). A class
              made from a template opens its form on the template's pattern and
              holidays until it has a term of its own. Keyed by the term's
              revision, so a change that lands closes it again. */}
          {assigned && (
            <ClassScheduleBuilder
              key={`${cls.schedule_timezone?.zone || "local"}:${cls.term_revision || 0}`}
              classId={classId}
              zone={cls.schedule_timezone || null}
              initial={(cls.assigned_occurrences || []).length ? null : cls.term_draft || null}
              current={detail.term || null}
              scheduled={(cls.assigned_occurrences || []).filter((o) => o.status !== "cancelled").length}
              hasPast={past.length > 0}
              onScheduled={load}
            />
          )}
          {assigned && <AssignedClassCalendar cls={cls} enabled={detail.calendar_publishing_enabled} onChanged={load} />}
          {!assigned && <OrganizationClassScheduling cls={cls} onChanged={load} />}
          {/* The way out, last (O5): archive keeps everything and stops the
              calendar; it sits where Delete would, at the bottom of Settings. */}
          <ClassArchiveCard cls={cls} onChanged={load} />
        </>
      )}

      {cls.program_id && (
        <AnnouncementDialog
          open={announcing}
          onOpenChange={setAnnouncing}
          programId={cls.program_id}
          programTitle={detail.program_title}
          classes={[{ class_id: classId, name: cls.name, instructor_name: detail.instructor_name }]}
          presetClassId={classId}
        />
      )}
            {!usesNamesRoster && <ImportRosterDialog
        open={importing}
        onOpenChange={setImporting}
        classId={classId}
        className={cls.name}
        onImported={load}
      />}
      <AssignSubstituteDialog
        open={Boolean(assigning)}
        onOpenChange={(v) => setAssigning(v ? assigning : null)}
        classId={classId}
        session={assigning}
        substitutes={substitutes}
        excludeUserIds={activeInstructors.map((i) => i.user_id)}
        onAssigned={load}
      />
      <SubstituteLessonDialog
        open={Boolean(subLesson)}
        onOpenChange={(v) => !v && setSubLesson(null)}
        classId={classId}
        className={cls.name}
        date={subLesson?.date}
        onChanged={load}
      />
      <MeetingDetailsDialog
        key={picked ? picked.occurrence_id || `${picked.date}-${picked.time_local}` : "none"}
        meeting={
          picked && {
            title: cls.name,
            program: detail.program_title,
            date: picked.date,
            time_local: picked.time_local,
            duration_minutes: picked.duration_minutes,
            status: picked.status,
            instructors: taughtBy,
            substitute: picked.substitute ? coverName(picked.substitute) : undefined,
            location: addressLine(cls.location_address),
            room: cls.room,
            tags: [
              picked.substitute ? { label: "Substitute", accent: true } : null,
              picked.prep_note ? { label: "Note" } : null,
              picked.plan ? { label: "Plan" } : null,
            ].filter(Boolean),
          }
        }
        onClose={() => setPicked(null)}
        edit={
          picked && assigned && picked.occurrence_id && !cls.archived_at
            ? {
                notice: "Moving a meeting doesn't notify anyone. Cancelling one tells its instructor.",
                onSave: (changes) => saveMeeting(picked, changes, "Meeting moved"),
                onCancel: () => saveMeeting(picked, { status: "cancelled" }, "Meeting cancelled"),
                onRestore: () => saveMeeting(picked, { status: "scheduled" }, "Meeting restored"),
              }
            : undefined
        }
        locked={
          picked && !(assigned && picked.occurrence_id)
            ? assigned
              ? "This meeting comes from the class's weekly schedule. Change it under Settings."
              : "This class is booked through Kalver Scheduler, so its meetings are changed there."
            : undefined
        }
        open={
          picked?.occurrence_id
            ? { label: "Prep note & plan", onPress: () => { setEditingMeeting(picked); setPicked(null); } }
            : picked && picked.status !== "cancelled"
              ? { label: "Assign substitute", onPress: () => { setAssigning(picked); setPicked(null); } }
              : undefined
        }
      />
      {editingMeeting && <AssignedMeetingEditor key={editingMeeting.occurrence_id} cls={cls} meeting={editingMeeting}
        onClose={() => setEditingMeeting(null)}
        /* A plan attached from the editor refreshes the page behind it and
           leaves the editor open; a saved change closes it first. */
        onChanged={(opts) => { if (opts?.keepOpen) loadMaterials(); load(); }}
        onSubstitute={() => { setAssigning(editingMeeting); setEditingMeeting(null); }} />}
    </PageShell>
  );
}
